/**
 * ORR-323 — Escribir en la faceta la categoría elegida al presentar la recategorización.
 *
 * Corre DESPUÉS de que la RPC marcó el vencimiento como presentado, así que nunca lanza:
 * devuelve `ok` para que el hook avise si falló, y `cambio` para el aviso a responsables
 * (ORR-351). La escritura no depende de la lectura previa: si no se pudo leer, se escribe
 * igual y lo único que se pierde es el aviso, no el dato.
 *
 * Vive fuera del hook para probarse sin React, como `notificarCambiosCategoria`.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { CategoriaMonotributo } from '@/core/components/calendario/mensajeRecategorizacion'
import { clientesService } from '@/services/clientesService'
import { decidirCambioCategoria, leerCategoriaActual } from '@/hooks/useSincronizarPadronCliente'

type Client = SupabaseClient<Database>

export interface ParamsCategoriaPresentada {
  clienteId: string
  organizacionId: string
  categoria: CategoriaMonotributo
}

export interface ResultadoCategoriaPresentada {
  ok: boolean
  /** Solo cuando se pudo leer la previa y difiere de la elegida. */
  cambio: { clienteId: string; anterior: string | null; nueva: string } | null
}

export interface DepsCategoriaPresentada {
  leer: (client: Client, clienteId: string) => Promise<string | null | undefined>
  escribir: (
    client: Client,
    clienteId: string,
    organizacionId: string,
    patch: Record<string, unknown>,
  ) => Promise<void>
}

const depsReales: DepsCategoriaPresentada = {
  leer: leerCategoriaActual,
  escribir: (client, clienteId, organizacionId, patch) =>
    clientesService.actualizarFacetaContable(client, clienteId, organizacionId, patch),
}

export const aplicarCategoriaPresentada = async (
  client: Client,
  { clienteId, organizacionId, categoria }: ParamsCategoriaPresentada,
  deps: DepsCategoriaPresentada = depsReales,
): Promise<ResultadoCategoriaPresentada> => {
  // `presentacion` es uno de los tres valores del CHECK de ORR-233; el trigger lo copia al
  // historial junto con el cambio.
  const patch = { categoria_monotributo: categoria, categoria_origen: 'presentacion' }

  const previa = await deps.leer(client, clienteId).catch(() => undefined)
  try {
    await deps.escribir(client, clienteId, organizacionId, patch)
  } catch (err) {
    console.error('No se pudo actualizar la categoría al presentar la recategorización:', err)
    return { ok: false, cambio: null }
  }
  return { ok: true, cambio: decidirCambioCategoria(clienteId, previa, patch) }
}
